import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CatalogOffering, CatalogService, InventorySerial, ResolveRmaPayload, RmaRequest
} from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-rma-request-detail',
  imports: [
    AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule, InputTextModule,
    InputNumberModule, SelectModule, TextareaModule, TableModule
  ],
  template: `
    <ain-page [title]="isCreateMode() ? t('catalog.rma.requests.createTitle') : t('catalog.rma.requests.detailTitle')" [description]="t('catalog.rma.requests.detailDescription')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (!isCreateMode() && !request()) {
        <ain-state type="error" [title]="t('catalog.unavailable')" [message]="error() || t('catalog.rma.requests.notFound')" />
      } @else {
        <section class="header-card">
          <div>
            <h3>{{ isCreateMode() ? t('catalog.rma.requests.newRecord') : request()!.requestNumber }}</h3>
            <p>{{ isCreateMode() ? t('catalog.rma.requests.createHint') : request()!.status }}</p>
            @if (!isCreateMode()) {
              <p class="warranty-badge" [class.active]="request()!.isUnderWarranty">
                {{ request()!.isUnderWarranty ? t('catalog.warranty.underWarranty') : t('catalog.warranty.outOfWarranty') }}
              </p>
            }
          </div>
          <div class="actions">
            <a [routerLink]="['/services/rma']">{{ t('catalog.action.backToList') }}</a>
            @if (isCreateMode()) {
              <button pButton type="button" [label]="t('catalog.action.save')" [disabled]="saving()" (click)="save()"></button>
            }
            @if (!isCreateMode() && canDiagnose()) {
              <button pButton type="button" [label]="t('catalog.rma.action.diagnose')" severity="info" [disabled]="acting()" (click)="diagnose()"></button>
            }
            @if (!isCreateMode() && canResolve()) {
              <button pButton type="button" [label]="t('catalog.rma.action.resolve')" severity="warn" [disabled]="acting()" (click)="resolve()"></button>
            }
            @if (!isCreateMode() && canApprove()) {
              <button pButton type="button" [label]="t('catalog.rma.action.approve')" severity="success" [disabled]="acting()" (click)="approve()"></button>
            }
          </div>
        </section>

        @if (isCreateMode()) {
          <section class="form-grid">
            <label>
              <span>{{ t('catalog.rma.requestNumber') }}</span>
              <input pInputText type="text" [(ngModel)]="form.requestNumber" />
            </label>
            <label>
              <span>{{ t('catalog.rma.reasonCode') }}</span>
              <input pInputText type="text" [(ngModel)]="form.reasonCode" />
            </label>
            <label>
              <span>{{ t('catalog.service.customerReference') }}</span>
              <input pInputText type="text" [(ngModel)]="form.customerReferenceNumber" />
            </label>
            <label>
              <span>{{ t('catalog.inventory.offeringId') }}</span>
              <p-select [options]="offeringOptions()" [(ngModel)]="form.offeringId" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" />
            </label>
            <label>
              <span>{{ t('catalog.tracking.serialId') }}</span>
              <p-select [options]="serialOptions()" [(ngModel)]="form.serialId" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" />
            </label>
            <label class="full">
              <span>{{ t('catalog.rma.description') }}</span>
              <textarea pTextarea rows="3" [(ngModel)]="form.description"></textarea>
            </label>
          </section>
        } @else {
          <section class="form-grid">
            <label>
              <span>{{ t('catalog.rma.diagnosisResultCode') }}</span>
              <input pInputText type="text" [(ngModel)]="diagnoseForm.diagnosisResultCode" [disabled]="!canDiagnose()" />
            </label>
            <label class="full">
              <span>{{ t('catalog.rma.diagnosisNotes') }}</span>
              <textarea pTextarea rows="2" [(ngModel)]="diagnoseForm.diagnosisNotes" [disabled]="!canDiagnose()"></textarea>
            </label>
          </section>

          @if (canResolve()) {
            <section class="form-grid resolve-panel">
              <label>
                <span>{{ t('catalog.rma.decisionType') }}</span>
                <p-select [options]="decisionTypeOptions()" [(ngModel)]="resolveForm.decisionType" optionLabel="label" optionValue="value" appendTo="body" />
              </label>
              @if (resolveForm.decisionType === 'Refund') {
                <label>
                  <span>{{ t('catalog.rma.refundAmount') }}</span>
                  <p-inputNumber [(ngModel)]="resolveForm.refundAmount" [min]="0.01" />
                </label>
              }
              @if (resolveForm.decisionType === 'Replace') {
                <label>
                  <span>{{ t('catalog.rma.replacementSerial') }}</span>
                  <p-select [options]="serialOptions()" [(ngModel)]="resolveForm.replacementSerialId" optionLabel="label" optionValue="value" appendTo="body" />
                </label>
              }
              <label class="full">
                <span>{{ t('catalog.rma.resolutionNotes') }}</span>
                <textarea pTextarea rows="2" [(ngModel)]="resolveForm.resolutionNotes"></textarea>
              </label>
            </section>
          }

          <section class="lines-panel">
            <h4>{{ t('catalog.rma.decisions.title') }}</h4>
            <p-table [value]="request()?.decisions ?? []">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('catalog.rma.decisionType') }}</th>
                  <th>{{ t('catalog.tracking.status') }}</th>
                  <th>{{ t('catalog.rma.refundAmount') }}</th>
                  <th>{{ t('catalog.rma.resolutionNotes') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.decisionType }}</td>
                  <td>{{ row.status }}</td>
                  <td>{{ row.refundAmount ?? '-' }}</td>
                  <td>{{ row.resolutionNotes || '-' }}</td>
                </tr>
              </ng-template>
            </p-table>
          </section>
        }

        @if (error()) {
          <p class="error">{{ error() }}</p>
        }
      }
    </ain-page>
  `,
  styles: [`
    .header-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; display: flex; justify-content: space-between; gap: 12px; }
    .header-card h3 { margin: 0; }
    .header-card p { margin: 6px 0 0; color: #64748b; }
    .warranty-badge { font-size: 12px; font-weight: 600; color: #b91c1c; }
    .warranty-badge.active { color: #15803d; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .form-grid { display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); padding-top: 10px; }
    .form-grid label { display: grid; gap: 6px; }
    .form-grid label.full { grid-column: 1 / -1; }
    .form-grid span { font-size: 12px; color: #475569; font-weight: 600; }
    .resolve-panel { border-top: 1px solid #e2e8f0; margin-top: 8px; padding-top: 12px; }
    .lines-panel { margin-top: 12px; }
    .lines-panel h4 { margin: 0 0 8px; font-size: 15px; }
    .error { margin: 10px 0 0; color: #b91c1c; }
  `]
})
export class RmaRequestDetailComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly acting = signal(false);
  readonly error = signal('');
  readonly request = signal<RmaRequest | null>(null);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly serials = signal<InventorySerial[]>([]);

  readonly form = {
    requestNumber: '',
    customerReferenceNumber: '',
    offeringId: '',
    serialId: '',
    reasonCode: '',
    description: ''
  };

  readonly diagnoseForm = { diagnosisResultCode: '', diagnosisNotes: '' };
  readonly resolveForm = {
    decisionType: 'Repair',
    resolutionNotes: '',
    refundAmount: 100,
    replacementSerialId: ''
  };

  readonly isCreateMode = computed(() => this.route.snapshot.paramMap.get('id') === 'new');
  readonly offeringOptions = computed(() => [
    { label: this.t('common.none'), value: '' },
    ...this.offerings().map(o => ({ label: `${o.code} - ${o.title}`, value: o.id }))
  ]);
  readonly serialOptions = computed(() => [
    { label: this.t('common.none'), value: '' },
    ...this.serials().map(s => ({ label: s.serialNumber, value: s.id }))
  ]);
  readonly decisionTypeOptions = computed(() => [
    { label: this.t('catalog.rma.decision.repair'), value: 'Repair' },
    { label: this.t('catalog.rma.decision.replace'), value: 'Replace' },
    { label: this.t('catalog.rma.decision.refund'), value: 'Refund' }
  ]);

  ngOnInit(): void {
    this.service.listOfferings().subscribe({ next: rows => this.offerings.set(rows) });
    this.service.listInventorySerials().subscribe({ next: rows => this.serials.set(rows) });
    this.load();
  }

  load(): void {
    if (this.isCreateMode()) {
      this.loading.set(false);
      return;
    }

    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.service.getRmaRequest(id).subscribe({
      next: request => {
        this.applyRequest(request);
        this.loading.set(false);
      },
      error: () => {
        this.request.set(null);
        this.error.set(this.t('catalog.rma.requests.notFound'));
        this.loading.set(false);
      }
    });
  }

  canDiagnose(): boolean {
    return this.request()?.status === 'Submitted';
  }

  canResolve(): boolean {
    return this.request()?.status === 'Diagnosed';
  }

  canApprove(): boolean {
    return this.request()?.status === 'PendingApproval';
  }

  save(): void {
    if (!this.isCreateMode()) return;
    if (!this.form.requestNumber.trim() || !this.form.reasonCode.trim()) {
      this.error.set(this.t('catalog.rma.requests.requiredFields'));
      return;
    }

    this.saving.set(true);
    this.error.set('');
    this.service.createRmaRequest({
      requestNumber: this.form.requestNumber.trim(),
      customerReferenceNumber: this.form.customerReferenceNumber.trim(),
      offeringId: this.form.offeringId || null,
      serialId: this.form.serialId || null,
      serviceOrderId: null,
      reasonCode: this.form.reasonCode.trim(),
      description: this.form.description.trim()
    }).subscribe({
      next: created => {
        this.saving.set(false);
        void this.router.navigate(['/services/rma', created.id]);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('catalog.rma.requests.createFailed'));
      }
    });
  }

  diagnose(): void {
    const request = this.request();
    if (!request || !this.diagnoseForm.diagnosisResultCode.trim()) {
      this.error.set(this.t('catalog.rma.diagnoseRequired'));
      return;
    }

    this.acting.set(true);
    this.error.set('');
    this.service.diagnoseRmaRequest(request.id, {
      diagnosisResultCode: this.diagnoseForm.diagnosisResultCode.trim(),
      diagnosisNotes: this.diagnoseForm.diagnosisNotes.trim()
    }).subscribe({
      next: updated => {
        this.applyRequest(updated);
        this.acting.set(false);
      },
      error: () => {
        this.acting.set(false);
        this.error.set(this.t('catalog.rma.diagnoseFailed'));
      }
    });
  }

  resolve(): void {
    const request = this.request();
    if (!request) return;

    const payload: ResolveRmaPayload = {
      decisionType: this.resolveForm.decisionType,
      resolutionNotes: this.resolveForm.resolutionNotes.trim(),
      refundAmount: this.resolveForm.decisionType === 'Refund' ? this.resolveForm.refundAmount : null,
      replacementSerialId: this.resolveForm.decisionType === 'Replace'
        ? (this.resolveForm.replacementSerialId || null)
        : null
    };

    this.acting.set(true);
    this.error.set('');
    this.service.resolveRmaRequest(request.id, payload).subscribe({
      next: updated => {
        this.applyRequest(updated);
        this.acting.set(false);
      },
      error: () => {
        this.acting.set(false);
        this.error.set(this.t('catalog.rma.resolveFailed'));
      }
    });
  }

  approve(): void {
    const request = this.request();
    if (!request) return;

    this.acting.set(true);
    this.error.set('');
    this.service.approveRmaRequest(request.id).subscribe({
      next: updated => {
        this.applyRequest(updated);
        this.acting.set(false);
      },
      error: () => {
        this.acting.set(false);
        this.error.set(this.t('catalog.rma.approveFailed'));
      }
    });
  }

  private applyRequest(request: RmaRequest): void {
    this.request.set(request);
    this.form.requestNumber = request.requestNumber;
    this.form.customerReferenceNumber = request.customerReferenceNumber;
    this.form.offeringId = request.offeringId ?? '';
    this.form.serialId = request.serialId ?? '';
    this.form.reasonCode = request.reasonCode;
    this.form.description = request.description;
    if (request.diagnosisResultCode) {
      this.diagnoseForm.diagnosisResultCode = request.diagnosisResultCode;
    }
    if (request.diagnosisNotes) {
      this.diagnoseForm.diagnosisNotes = request.diagnosisNotes;
    }
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
